import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  PaymentElement,
  useStripe,
  useElements,
  CardElement,
} from "@stripe/react-stripe-js";
import { geoNames } from "../../../../utils/countriesGeonames";
import { getCountryCode, getStatesByCountryCode } from "../../../../utils/countriesState";
import { useNavigate } from "react-router-dom";
import { CommonToast } from "../../../common/Toast";

export default function CheckoutForm({
  clientSecret,
  pendingSetup,
  plan,
  title,
  name,
  ShippingAddress,
  verifycompid,
  payLabel, // TIP-493 — e.g. "Pay Dh 1,088.10"
  isReportRequest,
}) {
  const stripe = useStripe();
  const navigate = useNavigate();
  const elements = useElements();
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [states, setStates] = useState();
  const [countries, setCountries] = useState(getCountryCode());
  const [currentCountry, setCurrentCountry] = useState();
  const [selectedCountry, setSelectedCountry] = useState("");
  const { userInfo } = useSelector((state) => state.auth);

  const fetchStates = async (code) => {
    try {
      setStates(ShippingAddress[0]?.state);
    } catch (error) {
    }
  };

  useEffect(() => {
    if (!stripe) {
      return;
    }

    if (!clientSecret) {
      return;
    }
    const paymentElement = elements.getElement("payment");
    paymentElement?.on("change", handlePaymentElementChange);
    if (userInfo?.email) {
      setEmail(userInfo?.email);
    }

    if (ShippingAddress && ShippingAddress[0]?.country) {
      const selectedCountryData = countries.find(
        (i) => i?.countryCode === ShippingAddress[0]?.country
      );
      setCurrentCountry(selectedCountryData?.countryName);

      if (selectedCountryData) {
        fetchStates(selectedCountryData?.countryCode);
      }
    }
  }, [
    ShippingAddress,
    userInfo?.email,
    clientSecret,
    elements,
    stripe,
    countries,
  ]);

  const handlePaymentElementChange = (event) => {
    const country = event.value.country;
    setSelectedCountry(country);
  };

  const handleSubmit = async (e) => {
    if (ShippingAddress) {
      localStorage.setItem("billing_add_ID", ShippingAddress[0]?.billing_add_ID)
      localStorage.setItem("userInfo", JSON.stringify(userInfo));
    }
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }
    setIsLoading(true);
    const returnUrlBase = `${window.location.origin}/plans/paymentsuccessful`;

    const response = await (pendingSetup ? stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url:
          title === "Verify Business"
            ? `${returnUrlBase}?verified=success&companyforverify=${plan?.slug}&forverifycompid=${verifycompid}`
            : returnUrlBase,
        // receipt_email: email,
        // shipping: {
        //   name: name,
        //   address: {
        //     line1: ShippingAddress && ShippingAddress[0]?.address_line_1,
        //     line2: ShippingAddress && ShippingAddress[0]?.address_line_2,
        //     state: states,
        //     city: ShippingAddress && ShippingAddress[0]?.city,
        //     country: currentCountry,
        //     postal_code: ShippingAddress && ShippingAddress[0]?.postal_code,
        //   },
        // },
      },
    }) : stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url:
          title === "Verify Business"
            ? `${returnUrlBase}?verified=success&companyforverify=${plan?.slug}&forverifycompid=${verifycompid}`
            : returnUrlBase,
        receipt_email: email,
        shipping: {
          name: name,
          address: {
            line1: ShippingAddress && ShippingAddress[0]?.address_line_1,
            line2: ShippingAddress && ShippingAddress[0]?.address_line_2,
            state: states,
            city: ShippingAddress && ShippingAddress[0]?.city,
            country: currentCountry,
            postal_code: ShippingAddress && ShippingAddress[0]?.postal_code,
          },
        },
      },
    }))
    if (response?.error) {
      CommonToast.error(response?.error.message)
      if (response?.error.type === "card_error" || response?.error.type === "validation_error") {
        setMessage(response?.error.message);
      } else {
        setMessage("An unexpected error occurred.");
      }
      // TIP-564 — a declined/failed payment resolves in place (Stripe doesn't
      // redirect on error), so the user can abandon here without ever
      // reaching SuccessfulPayment's cleanup. Skip this for an in-progress
      // report order: SuccessfulPayment re-reads createWorldboxCompany from
      // localStorage on mount to pick its copy/button, so clearing here would
      // show the wrong success screen if the user retries and it goes through.
      if (!isReportRequest) {
        localStorage.removeItem("createWorldboxCompany");
        localStorage.removeItem("companyDescription");
      }
    } else if (response?.paymentIntent.status === "succeeded") {
      setMessage("Payment succeeded!");
    } else if (response?.paymentIntent.status === "processing") {
      setMessage("Your payment is processing.");
    } else if (response?.paymentIntent.status === "requires_payment_method") {
      setMessage("Your payment was not successful, please try again.");
    } else {
      setMessage("Something went wrong.");
    }

    setIsLoading(false);
  };

  // TIP-646 — seed Stripe's billing-country field from the SELECTED billing
  // address. Left to itself the PaymentElement falls back to the Stripe
  // account's own country (AE), so a buyer with an Oslo address saw "United
  // Arab Emirates" under their card while the total correctly showed no VAT.
  // defaultValues only applies on mount, which is fine: changing address tears
  // the Elements down and remounts it with the new quote's client secret.
  const paymentElementOptions = {
    layout: "tabs",
    defaultValues: {
      billingDetails: {
        address: {
          country: ShippingAddress?.[0]?.country || undefined,
          city: ShippingAddress?.[0]?.city || undefined,
          postal_code: ShippingAddress?.[0]?.postal_code || undefined,
        },
      },
    },
  };

  return (
    <form id="payment-form" onSubmit={handleSubmit}>
      <PaymentElement id="payment-element" options={paymentElementOptions} />
      <button
        className="w-full bg-primary font-sans text-white  p-2 rounded-md mt-4"
        disabled={isLoading || !stripe || !elements}
        id="submit"
      >
        <span id="button-text">
          {isLoading ? "Loading..." : payLabel || "Process Payment"}
        </span>
      </button>

      {message && <div id="payment-message">{message}</div>}
    </form>
  );
}
